import { useLayoutEffect, type RefObject } from 'react'

/**
 * La pastilla que se desliza bajo la pestaña activa de un control segmentado.
 *
 * Escribe en la barra `--tab-x` y `--tab-w` —la posición y el ancho de la
 * pestaña activa— y el CSS mueve la pastilla con ellos. Vivía copiado en cada
 * ficha, y cada copia animaba TODO cambio de medida, que es de donde salía la
 * sensación de algo que se reajusta:
 *   · al abrir, la pastilla crecía desde ancho cero, porque leer `offsetLeft`
 *     fija el estilo inicial antes de poner las variables y la transición
 *     arrancaba desde ahí;
 *   · el observador de tamaño —que se crea de nuevo en cada cambio de pestaña y
 *     dispara nada más observar— volvía a colocarla a mitad del recorrido.
 *
 * Aquí sólo se anima lo que es un gesto: pasar de una pestaña a otra. Abrir,
 * que la barra cambie de ancho o que lleguen las fuentes coloca la pastilla de
 * golpe, sin transición. El CSS sólo anima con `data-tab-animate` en la barra,
 * que se enciende un fotograma después de la primera colocación.
 *
 * `target` es la barra, o un ref a ella: con ref de objeto, la barra se lee
 * dentro del efecto, que es cuando ya existe. `activeKey` es la pestaña
 * elegida; cuando cambia, se vuelve a medir.
 */
export function useTabThumb(target: RefObject<HTMLElement | null> | HTMLElement | null, activeKey: unknown): void {
  useLayoutEffect(() => {
    const bar = target && 'current' in target ? target.current : target
    if (!bar) return

    const place = (animate: boolean) => {
      const active = bar.querySelector<HTMLElement>('[role="tab"].is-active')
      if (!active) return
      const x = `${active.offsetLeft}px`
      const w = `${active.offsetWidth}px`
      /* Nada que mover: sin esto, una medida repetida apagaba la transición
         que estaba en curso. */
      if (bar.style.getPropertyValue('--tab-x') === x && bar.style.getPropertyValue('--tab-w') === w) return
      if (!animate) bar.removeAttribute('data-tab-animate')
      bar.style.setProperty('--tab-x', x)
      bar.style.setProperty('--tab-w', w)
      if (!animate) {
        /* Que el salto se aplique sin transición antes de volver a encenderla. */
        void bar.offsetWidth
        requestAnimationFrame(() => bar.setAttribute('data-tab-animate', ''))
      }
    }

    /* La primera vez de golpe; después, cada cambio de pestaña se desliza. */
    place(bar.hasAttribute('data-tab-animate'))

    /* Sólo cuando la barra cambia de verdad de ancho: el observador avisa
       también nada más empezar a observar, y eso no es un cambio. */
    let width = bar.offsetWidth
    const observer = new ResizeObserver(() => {
      if (bar.offsetWidth === width) return
      width = bar.offsetWidth
      place(false)
    })
    observer.observe(bar)
    /* Con la fuente de verdad los rótulos miden otra cosa. */
    void document.fonts?.ready?.then(() => place(false))
    return () => observer.disconnect()
  }, [target, activeKey])
}
